import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { useSession } from '@/context/SessionProvider'
import { useAppToast } from '@/hooks/useAppToast'
import { Grid, Box, Button, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { GameRules } from 'types/types'
import { useRunesStore, currenciesSettings } from './RunesProvider'
import { GameStatus, GAME_RISK, GAME_TYPE } from './types'
import { useApolloClient } from '@apollo/client'
import { delay } from '@/helpers/delay'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { memo, useEffect } from 'react'
import { RUNES_CONTRACT_ADDRESS } from '@/constants/constants'
import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByDenom } from '@/services/token-service'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown/CurrencyDropdown'
import { StyledInput2 } from '@/components/atoms/StyledInput/StyledInput'
import { AmountButtons } from '@/components/atoms/AmountButtons/AmountButtons'
import { placeBetTransaction } from '@/services/GameBroadcaster'
import { getRunesGameResult } from '@/services/api-game'
import { useTranslation } from 'next-i18next/pages'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'

export const Form = memo(() => {
  const { status, setValues, form } = useRunesStore()
  const denom = useBalanceStore((state) => state.denom)
  const { t } = useTranslation()

  useEffect(() => {
    useRunesStore.getState().setDenom(denom)

    return () => {
      useRunesStore.setState({ isCancelled: true })
    }
  }, [denom])

  const isDisabled = status === GameStatus.ACTIVE
  const maxValue = form.fields.maxValue
  const amount = form.fields.amount
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const isUsdc = getTokenByDenom(denom)?.currency === SupportedCurrencies.USDC
  const maxInfoText =
    isUsdc && !isUsd
      ? t('common.max') + ' ' + usdcToFiat(maxValue).toFixed(2) + ' ' + getSymbol()
      : t('common.max') + ' ' + maxValue.toFixed(2)

  return (
    <Grid
      gap={7}
      gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
      bg={'ninja.600'}
      p={{ base: 4, md: 5 }}
      rounded={'2xl'}
    >
      <Box>
        <Box hideFrom="md" mb={4}>
          <PlayButton />
        </Box>
        <FormTitle isDisabled={isDisabled} />
        <Box mb={2}>
          <StyledInput2
            clearable={true}
            title={t('common.amount')}
            placeholder={t('common.enter-amount')}
            icon={getTokenByDenom(denom).icon()}
            setValue={(value) => {
              setValues({ amount: { value, setDirty: true } })
            }}
            errors={form.errors}
            isDirty={form.isDirty}
            amount={amount}
            infoText={maxInfoText}
            isDisabled={isDisabled}
            denom={denom}
          />
        </Box>
        <AmountButtons
          maxValue={maxValue}
          denom={denom}
          setAmount={(value) => setValues({ amount: { value, setDirty: true } })}
          amount={amount}
          disabled={isDisabled}
        />
        <Box hideBelow="md" mt={2}>
          <GameInfo rtp="97%" />
        </Box>
      </Box>
      <Box>
        <GameMode isDisabled={isDisabled} />
        <Box hideBelow="md" mb={4}>
          <PlayButton />
        </Box>
      </Box>
      <Box hideFrom="md" gridColumn="1" transform="translateY(-20px)" mb={-5}>
        <GameInfo rtp="97%" />
      </Box>
    </Grid>
  )
})

const PlayButton = () => {
  const { signIn, account } = useSession()
  const { status, isCancelled, form, setGameActive, handleEndGame } = useRunesStore()
  const { onError } = useAppToast()
  const queryApolloClient = useApolloClient()
  const balanceStore = useBalanceStore()
  const { t } = useTranslation()

  const { mutate } = useMutation({
    mutationFn: async () => {
      if (!account) {
        signIn()
        return
      }

      useRunesStore.setState({ isCancelled: false })

      balanceStore.lockBalance()
      while (true) {
        useRunesStore.getState().setGameState([0, 0, 0, 0, 0], false)

        setGameActive()

        const tx = await placeBetTransaction({
          contract_addr: RUNES_CONTRACT_ADDRESS,
          funds: {
            denom: form.fields.denom,
            amount: form.fields.amount,
          },
          msg: {
            risk: form.fields.risk,
          },
        })

        if (!tx || tx?.code != 0) {
          throw Error()
        }

        const event = tx.events?.find((e) => e.type === 'wasm-runes_v1_game_created')
        const gameId = event?.attributes?.find((a: any) => a.key === 'game_id')?.value
        const random_round = event?.attributes?.find((a: any) => a.key === 'random_round')?.value
        const game_result = event?.attributes?.find((a: any) => a.key === 'internal_game_result')?.value

        const gameTx = {
          gameId,
          random_round,
          game_result,
        }

        const getResult = gameTx.game_result || (await getRunesGameResult(gameTx!.gameId, gameTx!.random_round))

        await useRunesStore.getState().setGameState(
          getResult.map((t: number) => t + 1),
          true
        )

        const gameType = useRunesStore.getState().form.fields.gameType
        if (gameType === GAME_TYPE.MANUAL || useRunesStore.getState().isCancelled) {
          break
        }

        await delay(750)
        balanceStore.unLockBalance(true)
        balanceStore.lockBalance()
      }
      handleEndGame()
    },
    onError: (e) => {
      console.error(e)
      handleEndGame()
      onError({ custom: { title: t('messages.something-went-wrong') } })
    },
    onSettled: () => {
      balanceStore.unLockBalance(true)
      queryApolloClient.refetchQueries({
        include: ['GET_RUNES_TICKETS_BY_USER'],
      })
    },
  })

  if (form.fields.gameType == GAME_TYPE.AUTO && status == GameStatus.ACTIVE && !isCancelled) {
    return (
      <Button
        onClick={() => {
          useRunesStore.setState({ isCancelled: true })
        }}
        variant={'orange'}
        rounded={'2xl'}
        w={'full'}
        h={'56px'}
        fontSize={'lg'}
      >
        {t('common.cancel')}
      </Button>
    )
  }

  return (
    <Button
      isDisabled={status == GameStatus.ACTIVE}
      onClick={() => mutate()}
      rounded={'2xl'}
      w={'full'}
      h={'56px'}
      fontSize={'lg'}
    >
      {t('common.play')}
    </Button>
  )
}

const GameMode = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useTranslation()
  const { form, setValue } = useRunesStore()
  const { play } = useAudio()
  const mode = form.fields.risk
  const gameType = form.fields.gameType

  const handleChangeRisk = (mode: GAME_RISK) => {
    if (!isDisabled) {
      setValue('risk', mode)

      play(SOUNDS.SET_AMOUNT)
    }
  }

  const handleChangeGameType = (gameType: GAME_TYPE) => {
    if (!isDisabled) {
      setValue('gameType', gameType)

      play(SOUNDS.SET_AMOUNT)
    }
  }

  return (
    <Box mb={2}>
      <Flex mb={2} justifyContent={'space-between'}>
        <Text fontWeight={700}>{t('common.game-mode')}</Text>
        <RulesButton
          styles={{
            h: 7,
            rounded: 'lg',
            fontSize: 'xs',
            py: 0,
            px: 4,
          }}
          game={GameRules.RUNES}
        />
      </Flex>

      <Flex mb={2} justifyContent={'space-between'}>
        <SimpleGrid
          opacity={isDisabled ? 0.6 : 1}
          gap={{ base: 0.5, sm: 1 }}
          p={1}
          h={10}
          fontSize={'sm'}
          fontWeight={700}
          bg={'ninja.500'}
          rounded={'xl'}
          columns={3}
        >
          <Flex
            onClick={() => handleChangeRisk(GAME_RISK.LOW)}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            rounded={'lg'}
            bg={mode === GAME_RISK.LOW ? '#3D3C70' : 'transparent'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text
              fontSize={{ base: 'xs', sm: 'sm', md: 'xs', lg: 'sm' }}
              color={mode !== GAME_RISK.LOW ? 'ninja.300' : 'white'}
            >
              {t('common.low')}
            </Text>
          </Flex>
          <Flex
            onClick={() => handleChangeRisk(GAME_RISK.MEDIUM)}
            px={2}
            bg={mode === GAME_RISK.MEDIUM ? '#3D3C70' : 'transparent'}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text
              fontSize={{ base: 'xs', sm: 'sm', md: 'xs', lg: 'sm' }}
              color={mode !== GAME_RISK.MEDIUM ? 'ninja.300' : 'white'}
            >
              {t('common.medium')}
            </Text>
          </Flex>
          <Flex
            onClick={() => handleChangeRisk(GAME_RISK.HIGH)}
            bg={mode === GAME_RISK.HIGH ? '#3D3C70' : 'transparent'}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text
              fontSize={{ base: 'xs', sm: 'sm', md: 'xs', lg: 'sm' }}
              color={mode !== GAME_RISK.HIGH ? 'ninja.300' : 'white'}
            >
              {t('common.high')}
            </Text>
          </Flex>
        </SimpleGrid>

        <SimpleGrid
          opacity={isDisabled ? 0.6 : 1}
          gap={1}
          p={1}
          h={10}
          fontSize={'sm'}
          fontWeight={700}
          bg={'ninja.500'}
          rounded={'xl'}
          columns={2}
        >
          <Flex
            onClick={() => handleChangeGameType(GAME_TYPE.MANUAL)}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            rounded={'lg'}
            px={2}
            bg={gameType === GAME_TYPE.MANUAL ? '#3D3C70' : 'transparent'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text
              fontSize={{ base: 'xs', sm: 'sm', md: 'xs', lg: 'sm' }}
              color={gameType !== GAME_TYPE.MANUAL ? 'ninja.300' : 'white'}
            >
              {t('common.manual')}
            </Text>
          </Flex>
          <Flex
            onClick={() => handleChangeGameType(GAME_TYPE.AUTO)}
            bg={gameType === GAME_TYPE.AUTO ? '#3D3C70' : 'transparent'}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text
              fontSize={{ base: 'xs', sm: 'sm', md: 'xs', lg: 'sm' }}
              color={gameType !== GAME_TYPE.AUTO ? 'ninja.300' : 'white'}
            >
              {t('common.auto')}
            </Text>
          </Flex>
        </SimpleGrid>
      </Flex>
    </Box>
  )
}

const FormTitle = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useTranslation()
  return (
    <Box mb={2}>
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Text userSelect={'none'} fontWeight={700}>
          {t('common.your-bet')}
        </Text>
        <Flex alignItems={'center'} gap={2}>
          <CurrencyDropdown isDisabled={isDisabled} />
        </Flex>
      </Flex>
    </Box>
  )
}
